import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Users } from "lucide-react";
import axios from "axios";
import { StudyCard, StudyCardSkeleton } from "../components/StudyCard";
import StudyDetailsModal from "../components/StudyDetailsModal";
import { BrandReviews } from "../components/BrandReviews";
import { FAQ } from "../components/FAQ";
import { HomeHero } from "../components/sections/HomeHero";
import { WhatAStudyLooksLike } from "../components/sections/WhatAStudyLooksLike";
import { PrivacyPromise } from "../components/sections/PrivacyPromise";
import { usePageMeta } from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudy, setSelectedStudy] = useState(null);

  usePageMeta({
    path: "/",
    title:
      "MediTalk , Paid healthcare research for patients & caregivers",
    description:
      "MediTalk pays patients and caregivers worldwide for taking part in healthcare research , interviews, focus groups and surveys. No long applications. Get involved on your terms.",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/studies?active_only=true`);
        if (!cancelled) setStudies(data.slice(0, 6));
      } catch (error) {
        console.error("Error fetching studies:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRegister = (study) => {
    // Open the study details modal so visitors can read the brief and
    // express interest without first joining the database , matches
    // the behaviour on /patient/studies and /caregiver/studies.
    setSelectedStudy(study);
  };

  return (
    <div>
      {/* 1. Hero , DARK */}
      <HomeHero />

      {/* 2. What a study looks like , WHITE */}
      <WhatAStudyLooksLike />

      {/* 4. Featured Studies , DARK */}
      <section className="bg-[var(--dark-bg)] grain-dark" data-testid="featured-studies">
        <div className="container-main py-16 md:py-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-4">
                <span className="w-8 h-px bg-[var(--lime)]" />
                Open studies
              </span>
              <h2 className="font-['Outfit'] font-bold text-white tracking-tight text-3xl md:text-4xl lg:text-5xl mb-3">
                Studies open
                <span className="text-[var(--lime)]"> right now.</span>
              </h2>
              <p className="text-[var(--gray-light)] text-lg leading-relaxed">
                Interviews, surveys and online bulletin boards across multiple
                conditions. Browse what&apos;s open and register your interest in
                under a minute.
              </p>
            </div>
            <Link
              to="/patient/studies"
              className="text-white font-medium flex items-center gap-2 hover:gap-3 transition-all shrink-0 self-start md:self-auto"
              data-testid="view-all-studies-link"
            >
              View all studies
              <ArrowRight size={18} className="text-[var(--lime)]" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              <>
                <StudyCardSkeleton />
                <StudyCardSkeleton />
                <StudyCardSkeleton />
              </>
            ) : studies.length > 0 ? (
              studies.map((study) => (
                <StudyCard
                  key={study.id}
                  study={study}
                  onRegister={handleRegister}
                  variant="dark"
                />
              ))
            ) : (
              <div className="col-span-3 text-center py-12">
                <p className="text-[var(--gray-light)]">
                  No studies available at the moment.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 5. Who can participate , WHITE */}
      <section className="bg-white" data-testid="who-can-participate">
        <div className="container-main py-16 md:py-24">
          <div className="max-w-2xl mb-10">
            <span className="badge badge-light mb-4">Who can join</span>
            <h2 className="font-['Outfit'] font-bold text-[var(--navy)] tracking-tight text-3xl md:text-4xl lg:text-5xl mb-3">
              If you have lived it, we want to hear it.
            </h2>
            <p className="text-[var(--text-muted)] text-lg leading-relaxed">
              Your day-to-day experience is more valuable to researchers than
              any dataset. Pick the path that describes you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/patient"
              data-testid="patient-card-link"
              className="group relative overflow-hidden rounded-2xl bg-white border border-[var(--light-border)] p-8 hover:border-[var(--teal)] hover:shadow-[0_16px_40px_-20px_rgba(5,150,105,0.35)] transition-all"
            >
              <div className="flex items-start gap-5">
                <span className="w-14 h-14 rounded-2xl bg-[var(--teal-soft)] text-[var(--teal-dark)] flex items-center justify-center shrink-0">
                  <Heart size={24} strokeWidth={1.8} />
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className="font-['Outfit'] font-semibold text-[var(--navy)] text-xl md:text-2xl mb-2 flex items-center gap-2">
                    I live with a medical condition
                    <ArrowRight
                      size={20}
                      className="text-[var(--teal)] group-hover:translate-x-1 transition-transform"
                    />
                  </h3>
                  <p className="text-[var(--text-muted)] leading-relaxed">
                    Share your firsthand experience: diagnosis, treatment,
                    everyday life, and help researchers understand what
                    patients actually need.
                  </p>
                </div>
              </div>
            </Link>

            <Link
              to="/caregiver"
              data-testid="caregiver-card-link"
              className="group relative overflow-hidden rounded-2xl bg-white border border-[var(--light-border)] p-8 hover:border-[var(--teal)] hover:shadow-[0_16px_40px_-20px_rgba(5,150,105,0.35)] transition-all"
            >
              <div className="flex items-start gap-5">
                <span className="w-14 h-14 rounded-2xl bg-[var(--teal-soft)] text-[var(--teal-dark)] flex items-center justify-center shrink-0">
                  <Users size={24} strokeWidth={1.8} />
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className="font-['Outfit'] font-semibold text-[var(--navy)] text-xl md:text-2xl mb-2 flex items-center gap-2">
                    I care for someone with a condition
                    <ArrowRight
                      size={20}
                      className="text-[var(--teal)] group-hover:translate-x-1 transition-transform"
                    />
                  </h3>
                  <p className="text-[var(--text-muted)] leading-relaxed">
                    Caregivers see what patients cannot describe alone. Your
                    perspective shapes better tools, better treatments, better
                    support.
                  </p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* 6. Privacy promise , LIGHT-CARD */}
      <PrivacyPromise />

      {/* 7. Testimonials , DARK (badge + link to dedicated page) */}
      <section className="bg-[var(--dark-bg)] grain-dark" data-testid="home-testimonials">
        <div className="container-main py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-4">
                <span className="w-8 h-px bg-[var(--lime)]" />
                Real voices
              </span>
              <h2 className="font-['Outfit'] font-bold text-white tracking-tight text-3xl md:text-4xl lg:text-5xl mb-4 leading-[1.05]">
                Real voices.
                <span className="text-[var(--lime)]"> In their own words.</span>
              </h2>
              <p className="text-[var(--gray-light)] text-lg leading-relaxed mb-8 max-w-xl">
                Patients and caregivers describe what taking part was actually
                like. Independently verified, unedited, in their own words.
              </p>
              <Link
                to="/reviews"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="read-all-reviews-btn"
              >
                Read all reviews
                <ArrowRight size={18} />
              </Link>
            </div>

            {/* Live rating badge , trust anchor */}
            <div className="lg:col-span-5 flex lg:justify-end">
              <BrandReviews mode="badge" />
            </div>
          </div>
        </div>
      </section>

      {/* 8. FAQ preview , WHITE */}
      <section className="bg-white" data-testid="home-faq">
        <div className="container-main py-16 md:py-24">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className="badge badge-light mb-4">Questions, answered</span>
              <h2 className="font-['Outfit'] font-bold text-[var(--navy)] tracking-tight text-3xl md:text-4xl lg:text-5xl mb-3">
                Most people ask us this first.
              </h2>
              <p className="text-[var(--text-muted)] text-lg leading-relaxed">
                If you cannot find your answer below, visit the full FAQ or get in touch.
              </p>
            </div>

            <FAQ limit={5} />

            <div className="text-center mt-8">
              <Link
                to="/faq"
                className="inline-flex items-center gap-2 text-[var(--teal)] hover:text-[var(--teal-dark)] font-semibold hover:gap-3 transition-all"
                data-testid="view-all-faq-link"
              >
                View all questions
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {selectedStudy && (
        <StudyDetailsModal
          study={selectedStudy}
          audience={selectedStudy.study_type === "caregiver" ? "caregiver" : "patient"}
          onClose={() => setSelectedStudy(null)}
        />
      )}
    </div>
  );
}
